import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import BottomNavBar from '../components/BottomNavBar';
import LoginPromptModal from '../components/LoginPromptModal';
import SEOMeta from '../components/SEOMeta';
import { useDongengFavorites } from '../hooks/useDongengFavorites';
import { useAuth } from '../contexts/AuthContext';

const PRIMARY  = '#2DBCAF';
const BG       = 'linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%)';
const API      = '/api/dongeng/videos';
const PER_PAGE = 5;

interface VideoItem {
  id: string;
  title: string;
  thumbnail: string;
  published: string;
}

// ─── Styled Components ────────────────────────────────────────────────────────
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const spin   = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;

const Page = styled.div`
  min-height: 100dvh;
  background: ${BG};
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding-bottom: 96px;
`;

const TopBar = styled.div`
  position: sticky; top: 0; z-index: 50;
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid #e2e8f0;
  display: flex; align-items: center; gap: 12px;
  padding: 14px 20px;
  max-width: 512px; margin: 0 auto;
`;

const BackBtn = styled.button`
  background: none; border: none; cursor: pointer;
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  color: #334155;
  &:hover { background: #f1f5f9; }
`;

const TopTitle = styled.h1`
  font-size: 17px; font-weight: 700; color: #1e293b; margin: 0; flex: 1;
`;

const Body = styled.div`
  max-width: 512px; margin: 0 auto; padding: 0 0 8px;
  animation: ${fadeIn} 0.4s ease;
`;

const LoadWrap = styled.div`
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; min-height: 60vh; gap: 12px;
  color: #94a3b8; font-size: 14px; font-family: 'Plus Jakarta Sans', sans-serif;
`;

const SpinIcon = styled.span`
  display: inline-block;
  animation: ${spin} 1s linear infinite;
  font-size: 32px; color: ${PRIMARY};
`;

// ─── Hero Player ─────────────────────────────────────────────────────────────
const HeroWrap = styled.div`
  position: relative; width: 100%; background: #000;
`;

const HeroThumb = styled.div<{ $src: string }>`
  width: 100%; aspect-ratio: 9/16; max-height: 72vh;
  background: url(${p => p.$src}) center/cover no-repeat;
  position: relative; cursor: pointer;
`;

const HeroOverlay = styled.div`
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 50%);
  display: flex; flex-direction: column; justify-content: flex-end;
  padding: 24px 20px;
`;

const PlayBtn = styled.button`
  position: absolute; top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 64px; height: 64px; border-radius: 50%;
  background: rgba(45,188,175,0.88);
  border: 3px solid rgba(255,255,255,0.6);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; backdrop-filter: blur(4px);
  transition: transform 0.15s, background 0.15s;
  &:hover { transform: translate(-50%,-50%) scale(1.08); background: ${PRIMARY}; }
  .material-symbols-outlined { color: #fff; font-size: 32px; }
`;

const HeroTitle = styled.h2`
  font-size: 18px; font-weight: 800; color: #fff; margin: 0 0 4px;
  line-height: 1.3; text-shadow: 0 2px 8px rgba(0,0,0,0.4);
`;

const NewBadge = styled.span`
  display: inline-block; background: ${PRIMARY}; color: #fff;
  font-size: 10px; font-weight: 700; letter-spacing: 0.05em;
  padding: 3px 9px; border-radius: 20px; margin-bottom: 8px;
`;

const IframeWrap = styled.div`
  width: 100%; aspect-ratio: 9/16; max-height: 72vh; background: #000;
  iframe { width: 100%; height: 100%; border: none; display: block; }
`;

const HeartBtn = styled.button<{ $active?: boolean }>`
  position: absolute;
  top: 12px; right: 12px;
  width: 36px; height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,0.92);
  border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; z-index: 5;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  transition: transform 0.15s;
  &:active { transform: scale(0.9); }
  .material-symbols-outlined {
    font-size: 20px;
    color: ${p => p.$active ? '#ef4444' : '#94a3b8'};
    font-variation-settings: ${p => p.$active
      ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24"
      : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"};
  }
`;

const RowHeartBtn = styled.button<{ $active?: boolean }>`
  background: none; border: none; padding: 4px;
  cursor: pointer; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  .material-symbols-outlined {
    font-size: 20px;
    color: ${p => p.$active ? '#ef4444' : '#cbd5e1'};
    font-variation-settings: ${p => p.$active
      ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24"
      : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"};
  }
`;

// ─── List ─────────────────────────────────────────────────────────────────────
const Section = styled.div`
  padding: 20px 16px 8px;
`;

const SectionTitle = styled.h3`
  font-size: 15px; font-weight: 800; color: #1e293b; margin: 0 0 12px;
  display: flex; align-items: center; gap: 6px;
  span { color: ${PRIMARY}; }
`;

const Grid = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`;

const VideoRow = styled.div<{ $active?: boolean }>`
  display: flex; gap: 12px; align-items: center;
  padding: 10px 12px;
  background: ${p => p.$active ? 'rgba(45,188,175,0.08)' : '#fff'};
  border: 1.5px solid ${p => p.$active ? PRIMARY : '#e2e8f0'};
  border-radius: 16px; cursor: pointer; transition: all 0.15s;
  &:hover { border-color: ${PRIMARY}; background: rgba(45,188,175,0.06); }
`;

const VThumb = styled.div<{ $src: string }>`
  width: 68px; height: 90px; border-radius: 10px;
  background: url(${p => p.$src}) center/cover no-repeat #e2e8f0;
  flex-shrink: 0; position: relative; overflow: hidden;
`;

const VThumbPlay = styled.div`
  position: absolute; inset: 0; display: flex; align-items: center;
  justify-content: center; background: rgba(0,0,0,0.2);
  .material-symbols-outlined { color: rgba(255,255,255,0.9); font-size: 22px; }
`;

const VMeta = styled.div`flex: 1; min-width: 0;`;

const VTitle = styled.p`
  font-size: 13px; font-weight: 700; color: #1e293b; margin: 0 0 5px;
  line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;

const VTag = styled.span`
  font-size: 10px; font-weight: 600; color: ${PRIMARY};
  background: rgba(45,188,175,0.1); padding: 2px 8px; border-radius: 20px;
`;

const LoadMoreBtn = styled.button`
  width: 100%; margin-top: 14px; padding: 14px;
  background: #fff; border: 2px solid ${PRIMARY}; border-radius: 16px;
  color: ${PRIMARY}; font-size: 14px; font-weight: 700;
  font-family: 'Plus Jakarta Sans', sans-serif; cursor: pointer;
  transition: all 0.15s;
  &:hover { background: ${PRIMARY}; color: #fff; }
`;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DongengPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const initVideoId = (location.state as any)?.videoId as string | undefined;
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useDongengFavorites();

  const [videos, setVideos]     = useState<VideoItem[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [playing, setPlaying]   = useState(false);
  const [visibleCount, setVisibleCount] = useState(PER_PAGE);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleHeart = async (e: React.MouseEvent, videoId: string) => {
    e.stopPropagation();
    if (!user) { setShowLoginPrompt(true); return; }
    await toggleFavorite(videoId);
  };

  useEffect(() => {
    fetch(API)
      .then(r => r.json())
      .then(d => {
        if (d.ok && d.videos?.length) {
          setVideos(d.videos);
          // If navigated from home with specific video, select & autoplay it
          if (initVideoId) {
            const idx = d.videos.findIndex((v: VideoItem) => v.id === initVideoId);
            if (idx >= 0) {
              setActiveIdx(idx);
              setPlaying(true);
              setVisibleCount(Math.max(PER_PAGE, idx + 1));
            }
          }
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  // Listen for YouTube iframe events (autoplay next)
  const handleMessage = useCallback((e: MessageEvent) => {
    try {
      const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      // YouTube sends {event:'onStateChange', info: 0} when video ends
      if (data?.event === 'onStateChange' && data?.info === 0) {
        setVideos(prev => {
          const nextIdx = activeIdx + 1;
          if (nextIdx < prev.length) {
            setActiveIdx(nextIdx);
            setPlaying(true);
            // Ensure next video is visible in list
            setVisibleCount(c => Math.max(c, nextIdx + 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          return prev;
        });
      }
    } catch (_) {}
  }, [activeIdx]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const handleSelect = (idx: number) => {
    setActiveIdx(idx);
    setPlaying(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePlay = () => setPlaying(true);

  if (loading) return (
    <Page>
      <LoadWrap>
        <SpinIcon className="material-symbols-outlined">autorenew</SpinIcon>
        <span>Memuat dongeng...</span>
      </LoadWrap>
      <BottomNavBar />
    </Page>
  );

  if (error || videos.length === 0) return (
    <Page>
      <LoadWrap>
        <span className="material-symbols-outlined" style={{ fontSize: 40 }}>wifi_off</span>
        <span>Gagal memuat video. Coba lagi nanti.</span>
      </LoadWrap>
      <BottomNavBar />
    </Page>
  );

  const hero = videos[activeIdx];
  const listVideos = videos.filter((_, i) => i !== activeIdx);
  const visible    = listVideos.slice(0, visibleCount);
  const hasMore    = visibleCount < listVideos.length;

  return (
    <Page>
      <SEOMeta
        title="Video Dongeng Anak - Cerita Edukatif untuk Balita"
        description="Koleksi video dongeng edukatif untuk anak usia 3-6 tahun. Cerita-cerita seru yang mengajarkan nilai moral dan pengetahuan dengan cara menyenangkan."
        path="/dongeng"
      />

      <Body>
        {/* ── Hero Player ── */}
        <HeroWrap>
          {playing ? (
            <IframeWrap>
              <iframe
                ref={iframeRef}
                src={`https://www.youtube-nocookie.com/embed/${hero.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </IframeWrap>
          ) : (
            <HeroThumb $src={hero.thumbnail} onClick={handlePlay}>
              <PlayBtn onClick={e => { e.stopPropagation(); handlePlay(); }}>
                <span className="material-symbols-outlined">play_arrow</span>
              </PlayBtn>
              <HeartBtn
                $active={isFavorite(hero.id)}
                onClick={e => handleHeart(e, hero.id)}
              >
                <span className="material-symbols-outlined">favorite</span>
              </HeartBtn>
              <HeroOverlay>
                {activeIdx === 0 && <NewBadge>✨ TERBARU</NewBadge>}
                <HeroTitle>{hero.title}</HeroTitle>
              </HeroOverlay>
            </HeroThumb>
          )}
        </HeroWrap>

        {/* ── Video List ── */}
        <Section>
          <SectionTitle>Selanjutnya <span>✦</span></SectionTitle>
          <Grid>
            {visible.map((v, i) => {
              const realIdx = videos.findIndex(x => x.id === v.id);
              return (
                <VideoRow key={v.id} $active={false} onClick={() => handleSelect(realIdx)}>
                  <VThumb $src={v.thumbnail}>
                    <VThumbPlay>
                      <span className="material-symbols-outlined">play_arrow</span>
                    </VThumbPlay>
                  </VThumb>
                  <VMeta>
                    <VTitle>{v.title}</VTitle>
                    <VTag>Dongeng</VTag>
                  </VMeta>
                  <RowHeartBtn
                    $active={isFavorite(v.id)}
                    onClick={e => handleHeart(e, v.id)}
                  >
                    <span className="material-symbols-outlined">favorite</span>
                  </RowHeartBtn>
                </VideoRow>
              );
            })}
          </Grid>

          {hasMore && (
            <LoadMoreBtn onClick={() => setVisibleCount(c => c + PER_PAGE)}>
              Tampilkan {Math.min(PER_PAGE, listVideos.length - visibleCount)} video lagi ↓
            </LoadMoreBtn>
          )}
        </Section>
      </Body>
      <BottomNavBar />
      {showLoginPrompt && (
        <LoginPromptModal
          onLogin={() => { setShowLoginPrompt(false); window.location.href = '/profile'; }}
          onClose={() => setShowLoginPrompt(false)}
          subtitle={<>Silakan login terlebih dahulu untuk<br />menyimpan dongeng favoritmu.</>}
        />
      )}
    </Page>
  );
}
